import { ContentButton, ContentLink, LoadStatus, portalStoreToken } from '@servicetitan/cp-ui';
import {
    Banner,
    BodyText,
    Button,
    Divider,
    Form,
    Headline,
    Icon,
    Stack,
} from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { AuthRegisterStore } from '../../stores/register.store';
import { enableAutoValidationOnValidationError } from '../../utils/field-auto-validate-helper';
import { handleEmailSpaceKeyDown } from '../../utils/field-email-helper';
import { useAuthHistory, useAuthRegisterQueryParams } from '../../utils/use-auth-history';
import { GoogleLogin } from '../google';
import { AuthLayout } from '../layout/auth.layout';
import { AuthHeadline } from '../layout/components';
// import { MicrosoftLogin } from '../microsoft';
import * as Styles from './register.module.less';

export const AuthRegisterProvider = provide({ singletons: [AuthRegisterStore] })(
    ({ children }) => children
);

export const AuthRegisterComponent = observer(() => {
    const [{ canSubmit, form, status, submit }, { settings }] = useDependencies(
        AuthRegisterStore,
        portalStoreToken
    );
    const { email } = form.$;
    const history = useAuthHistory();
    const contactType = 'email address';
    const { info } = useAuthRegisterQueryParams();

    return status === LoadStatus.Ok ? (
        <AuthLayout>
            <Stack justifyContent="center" className="m-b-2">
                <Icon name="check_circle" className="c-green-400" size={48} />
            </Stack>
            <Headline className="ta-center form-headline">Invite Link Sent</Headline>

            <BodyText subdued className={classNames('ta-center m-t-1 m-b-2', Styles.description)}>
                If your {contactType} is a match in our system, you will receive the instructions to
                finish setting up your account.
            </BodyText>

            <BodyText
                subdued
                italic
                className={classNames('ta-center p-t-2 border-top m-b-4', Styles.description)}
            >
                If you didn't receive sign in information please contact us at{' '}
                <span className={classNames('border-bottom', Styles.noWrap)}>
                    {settings?.phoneNumberFormatted}
                </span>
                .
            </BodyText>

            <ContentButton className="m-t-3" width="full" href={history.href('login')}>
                Back
            </ContentButton>
        </AuthLayout>
    ) : (
        <AuthLayout>
            {status === LoadStatus.Error ? (
                <Banner status="critical" icon title="Incorrect email" className="m-b-2" />
            ) : info === 'invite_expired' ? (
                <Banner status="warning" icon title="Link expired" className="m-b-2">
                    This invite link has expired. Please enter your information below to get started
                    or contact us for a new invitation.
                </Banner>
            ) : null}

            <AuthHeadline
                title="Register"
                description="Make payments, book appointments, view your work history, accept estimates and more."
            />

            <Stack direction="column" className="m-b-2" spacing={2}>
                <GoogleLogin />
                {/* <MicrosoftLogin /> */}
                <Divider className="m-t-4-i">
                    <BodyText subdued size="small" className="m-x-1">
                        or sign up to continue
                    </BodyText>
                </Divider>
            </Stack>

            <Form>
                <Form.Input
                    label="Email"
                    className="m-t-2"
                    value={email.value}
                    onChange={email.onChangeHandler}
                    onKeyDown={handleEmailSpaceKeyDown(email)}
                    onBlur={enableAutoValidationOnValidationError(email)}
                    error={email.error}
                    autoComplete="email"
                    type="email"
                />

                <Button
                    width="full"
                    primary
                    onClick={submit}
                    loading={status === LoadStatus.Loading}
                    disabled={!canSubmit}
                >
                    Sign up with Email
                </Button>

                <BodyText className="ta-center m-t-4">
                    Have an account?{' '}
                    <ContentLink href={history.href('login')} primary>
                        Sign In
                    </ContentLink>
                </BodyText>
            </Form>
        </AuthLayout>
    );
});

export const AuthRegister = () => (
    <AuthRegisterProvider>
        <AuthRegisterComponent />
    </AuthRegisterProvider>
);
